'use client';

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calculator, DollarSign, TrendingUp, TrendingDown, Calendar } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { debtCalculatorContent } from '../../data/debt-calculator-content';

export default function DebtCalculator() {
    const [principal, setPrincipal] = useState('');
    const [interestRate, setInterestRate] = useState('');
    const [loanTerm, setLoanTerm] = useState('');
    const [monthlyPayment, setMonthlyPayment] = useState('');
    const [totalPayment, setTotalPayment] = useState('');
    const [totalInterest, setTotalInterest] = useState('');
    const [isCalculating, setIsCalculating] = useState(false);

    const { toast } = useToast();

    // Calculate when inputs change
    useEffect(() => {
        if (principal && interestRate && loanTerm && monthlyPayment) {
            const p = parseFloat(principal);
            const r = parseFloat(interestRate) / 100 / 12;
            const n = parseFloat(loanTerm) * 12;
            const m = parseFloat(monthlyPayment);

            // Calculate total payment and total interest
            const total = m * n;
            const totalInterestCalculated = total - (p * (1 + r) ** n) / ((1 + r) ** n - 1);

            setTotalPayment(total.toFixed(2));
            setTotalInterest(totalInterestCalculated.toFixed(2));
        }
    }, [principal, interestRate, loanTerm, monthlyPayment]);

    const calculateFromPrincipal = () => {
        if (!principal || !interestRate || !loanTerm) {
            toast({
                title: 'Missing Information',
                description: 'Please enter principal, interest rate, and loan term.',
                variant: 'destructive',
            });
            return;
        }

        setIsCalculating(true);

        try {
            const p = parseFloat(principal);
            const r = parseFloat(interestRate) / 100 / 12;
            const n = parseFloat(loanTerm) * 12;

            // Calculate monthly payment using amortization formula
            const m = p * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);

            setMonthlyPayment(m.toFixed(2));
            setTotalPayment((m * n).toFixed(2));

            // Calculate total interest
            const totalInterestCalculated = (m * n) - p;
            setTotalInterest(totalInterestCalculated.toFixed(2));

            toast({
                title: 'Calculation Complete',
                description: `Monthly payment: $${m.toFixed(2)}`,
            });
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to calculate loan metrics.',
                variant: 'destructive',
            });
        } finally {
            setIsCalculating(false);
        }
    };

    const calculateFromMonthly = () => {
        if (!monthlyPayment || !interestRate || !loanTerm) {
            toast({
                title: 'Missing Information',
                description: 'Please enter monthly payment, interest rate, and loan term.',
                variant: 'destructive',
            });
            return;
        }

        setIsCalculating(true);

        try {
            const m = parseFloat(monthlyPayment);
            const r = parseFloat(interestRate) / 100 / 12;
            const n = parseFloat(loanTerm) * 12;

            // Calculate principal using amortization formula
            const p = m / ((r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1));

            setPrincipal(p.toFixed(2));
            setTotalPayment((m * n).toFixed(2));

            // Calculate total interest
            const totalInterestCalculated = (m * n) - p;
            setTotalInterest(totalInterestCalculated.toFixed(2));

            toast({
                title: 'Calculation Complete',
                description: `Principal: $${p.toFixed(2)}`,
            });
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to calculate loan metrics.',
                variant: 'destructive',
            });
        } finally {
            setIsCalculating(false);
        }
    };

    const resetCalculator = () => {
        setPrincipal('');
        setInterestRate('');
        setLoanTerm('');
        setMonthlyPayment('');
        setTotalPayment('');
        setTotalInterest('');
    };

    return (
        <>
            <Toaster />
            <div className="min-h-screen bg-gray-50 py-12 px-6 sm:px-8">
                <div className="max-w-6xl mx-auto">
                    <ToolPageIntro title="Debt Calculator - Free Online Loan Payment Calculator">
                Calculate loan payments, total interest, and amortization schedules with our free debt calculator. Plan your debt payoff strategy and understand loan costs.
            </ToolPageIntro>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Input Section */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Loan Details</CardTitle>
                                <CardDescription>
                                    Enter your loan information to calculate payments
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    <div>
                                        <Label htmlFor="principal">Principal Amount ($)</Label>
                                        <Input
                                            type="number"
                                            id="principal"
                                            value={principal}
                                            onChange={(e) => setPrincipal(e.target.value)}
                                            placeholder="10000"
                                            className="w-full"
                                        />
                                    </div>

                                    <div>
                                        <Label htmlFor="interest-rate">Annual Interest Rate (%)</Label>
                                        <Input
                                            type="number"
                                            id="interest-rate"
                                            value={interestRate}
                                            onChange={(e) => setInterestRate(e.target.value)}
                                            placeholder="5.5"
                                            step="0.1"
                                            className="w-full"
                                        />
                                    </div>

                                    <div>
                                        <Label htmlFor="loan-term">Loan Term (years)</Label>
                                        <Input
                                            type="number"
                                            id="loan-term"
                                            value={loanTerm}
                                            onChange={(e) => setLoanTerm(e.target.value)}
                                            placeholder="30"
                                            className="w-full"
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <Button
                                            onClick={calculateFromPrincipal}
                                            disabled={isCalculating}
                                            className="flex-1"
                                        >
                                            {isCalculating ? (
                                                <>
                                                    <Calculator className="h-4 w-4 mr-2 animate-spin" />
                                                    Calculating...
                                                </>
                                            ) : (
                                                <>
                                                    <DollarSign className="h-4 w-4 mr-2" />
                                                    Calculate Payment
                                                </>
                                            )}
                                        </Button>

                                        <Button
                                            variant="outline"
                                            onClick={resetCalculator}
                                        >
                                            <Calendar className="h-4 w-4 mr-2" />
                                            Reset
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Results Section */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Loan Summary</CardTitle>
                                <CardDescription>
                                    Your loan calculation results and amortization details
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    {monthlyPayment && (
                                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                            <div className="text-center">
                                                <DollarSign className="h-8 w-8 mx-auto text-blue-600 mb-2" />
                                                <h3 className="text-lg font-semibold text-blue-900 mb-1">
                                                    Monthly Payment
                                                </h3>
                                                <p className="text-2xl font-bold text-blue-600">
                                                    ${monthlyPayment}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {totalPayment && (
                                        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                            <div className="text-center">
                                                <TrendingUp className="h-8 w-8 mx-auto text-green-600 mb-2" />
                                                <h3 className="text-lg font-semibold text-green-900 mb-1">
                                                    Total Payment
                                                </h3>
                                                <p className="text-2xl font-bold text-green-600">
                                                    ${totalPayment}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {totalInterest && (
                                        <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                                            <div className="text-center">
                                                <TrendingDown className="h-8 w-8 mx-auto text-orange-600 mb-2" />
                                                <h3 className="text-lg font-semibold text-orange-900 mb-1">
                                                    Total Interest
                                                </h3>
                                                <p className="text-2xl font-bold text-orange-600">
                                                    ${totalInterest}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Informational Content */}
                    <div className="mt-12">
                        <InformationalContent
                            title={debtCalculatorContent.title}
                            description={debtCalculatorContent.description}
                            content={debtCalculatorContent.content}
                            faqs={debtCalculatorContent.faqs}
                            relatedTools={debtCalculatorContent.relatedTools}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}
